"use client";

import { useLocale, useTranslations } from "next-intl";

import { Link, routing, usePathname } from "@/i18n/routing";

type FooterProps = {
  alternateHref?: string;
  locale?: string;
};

const LANGUAGE_NAMES: Record<string, string> = {
  en: "EN",
  fr: "FR",
};

/**
 * Extracts the pathname from a full path, removing locale prefix if present.
 * Examples:
 * - '/fr/blog' -> '/blog'
 * - '/en/blog/my-post' -> '/blog/my-post'
 * - '/blog' -> '/blog'
 */
function extractPathnameFromHref(href: string): string {
  // Remove leading slash
  const withoutLeadingSlash = href.startsWith("/") ? href.slice(1) : href;

  // Check if it starts with a locale prefix
  const localePrefix = routing.locales.find((locale) =>
    withoutLeadingSlash.startsWith(`${locale}/`),
  );
  if (localePrefix) {
    return "/" + withoutLeadingSlash.slice(localePrefix.length + 1);
  }

  // No locale prefix found, return as-is
  return href.startsWith("/") ? href : `/${href}`;
}

/**
 * Extracts the target locale from a full path if it contains a locale prefix.
 * Returns undefined when the path lacks a locale prefix.
 */
function extractLocaleFromHref(href: string): string | undefined {
  return routing.locales.find((locale) => href.startsWith(`/${locale}/`));
}

export function Footer({ alternateHref }: FooterProps) {
  const t = useTranslations("language_switcher");
  const currentLocale = useLocale();
  const pathname = usePathname();
  const otherLocale = currentLocale === "en" ? "fr" : "en";
  const otherLangLabel =
    currentLocale === "en" ? t("switch_to_fr") : t("switch_to_en");

  // When alternateHref exists, extract pathname and determine target locale from the href
  // Otherwise, use current pathname with otherLocale
  const href = alternateHref
    ? extractPathnameFromHref(alternateHref)
    : pathname;
  // Determine target locale: if alternateHref contains a locale prefix, extract it; otherwise use otherLocale
  const targetLocale = alternateHref
    ? extractLocaleFromHref(alternateHref) || otherLocale
    : otherLocale;

  return (
    <footer className="border-t border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container mx-auto px-4 py-6 max-w-2xl">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          {/* Socials Username */}
          <div className="text-sm text-muted-foreground">
            <span>@bntvllnt</span>
          </div>

          {/* Language Switcher */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground uppercase tracking-wide">
              {LANGUAGE_NAMES[currentLocale] || currentLocale.toUpperCase()}
            </span>
            <Link
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              // @ts-expect-error - href can be dynamic pathname, but TypeScript infers route pattern types.
              // next-intl's Link handles string pathnames at runtime, including dynamic routes.
              href={href}
              locale={targetLocale}
            >
              {otherLangLabel}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
